/**
 * Copyright (c) 2020-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { register } from 'fetch-intercept'
import { Keychain } from './KeyChain'

export type Route = {
  method: string,
  path: string,
  aditionalHeaders?: boolean
}

export type Config = {
  application: string,
  clientId: string,
  keychain: Keychain,
  baseURL: string,
  ignoredURL: Route[],
  verbose: boolean,
  url401: string,
  url403: string,
  use403Redirect: boolean
}

/**
 * Responsável por interceptar as requisições feitas pelo `fetch(url, config)`
 * e adicionar o token de autenticação do sensedia
 *
 * @see https://www.npmjs.com/package/fetch-intercept
 * @example
 *  new FetchInterceptor({
 *    clientId: 'ffaecdbf-1c3e-3993-a716-8a9265f5af7b',
 *    application: 'AP-RESULTADO-DE-EXAME',
 *    keychain: new Keychain(),
 *    baseURL: 'http://localhost:3001',
 *    ignoredURL: [
 *      { method: 'GET', url: '/example' }
 *    ]
 * })
 */
export const interceptor = (config: Partial<Config>) => {
  config = Object.assign({
    baseURL: process.env.REACT_APP_API,
    ignoredURL: [],
    keychain: new Keychain(),
    use403Redirect: true
  }, config)

  return register({
    async request (url: string, init: RequestInit = {}): Promise<Response> {
      const hasIgnoredUrl = config.ignoredURL
        .map(service => url.includes(service.path) && service.method.toUpperCase() === init.method.toUpperCase())
        .includes(true)

      const ignoredUrlValues = config.ignoredURL.find((ignoredUrl) => {
        return url.includes(ignoredUrl.path)
      })

      if (ignoredUrlValues?.aditionalHeaders || !hasIgnoredUrl) {
        init.headers = {
          ...init.headers,
          'x-origin-channel': 'web',
          'x-origin-application': config.application
        }
      }

      if (!hasIgnoredUrl && url.includes(config.baseURL) && await config.keychain.hasToken()) {
        init.headers = {
          ...init.headers,
          client_id: config.clientId,
          access_token: await config.keychain.getToken()
        }
      }

      return Promise.resolve([url, init])
    },
    requestError (error: Error): Promise<any> {
      return Promise.reject(error)
    },
    async response (response: Response): Promise<Response> {
      const hasIgnoredUrl = config.ignoredURL
        .map(path => response.url.includes(path.url))
        .includes(true)

      if (!hasIgnoredUrl && response.status === 401) {
        if (config.url401) {
          window.location.href = config.url401
        } else {
          console.error('Need a callback url to 401 error')
        }
      }

      //usuário não positivado
      if (config.use403Redirect && !hasIgnoredUrl && response.status === 403) {
        if ((await response.json()).mensagem === 'Paciente não positivado'
          || (await response.json()).mensagem === 'Medico não positivado') {
            window.location.href = config.url403
        } else {
          //retorno do Sensedia
          if (config.url401) {
            window.location.href = config.url401
          } else {
            console.error('Need a callback url to 401 error')
          }
        }
      }
      return Promise.resolve(response)
    },
    responseError (error: Error): Promise<void> {
      return Promise.reject(error)
    }
  })
}
